// client/src/components/publishing/BulkSchedulePage.jsx
//
// /:tenant/media/bulk-schedule — schedule many posts from one CSV file
// (Roadmap Phase 3.3): download the template, upload a filled-in file, see
// every row checked (errors and warnings per row), then queue the posts.
// Nothing is saved until "Schedule"; the server re-checks the same file then.
// Times in the file are in the organization's time zone.
//
// API: /api/publishing/:tenant/bulk/* (server/routes/publishingRoutes.js).

import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaCalendarCheck, FaDownload, FaRss, FaUpload } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import { bulkTemplateUrl, validateBulk, commitBulk, errorText } from '../../services/publishingApi';
import { checkPlatformAuth } from '../../utils/platformAuthCheck';
import { slotLabel } from './BestTimeSuggestions';
import './Publishing.css';

const MAX_BYTES = 512 * 1024;

function readText(file) {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error);
    r.readAsText(file);
  });
}

export default function BulkSchedulePage({ tenantId: tenantProp }) {
  const { tenant: tenantParam } = useParams();
  const tenant = tenantParam || tenantProp;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [fileName, setFileName] = useState('');
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState(null);
  const [skipInvalid, setSkipInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [unconnected, setUnconnected] = useState([]);
  const inputId = 'pb-bulk-file';

  const reset = () => {
    setFileName(''); setCsv(''); setResult(null); setSkipInvalid(false); setError(''); setDone(null); setUnconnected([]);
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    reset();
    if (file.size > MAX_BYTES) {
      setError(t('publishing.bulk.tooLarge', 'That file is larger than 512 KB. Split it into smaller files.'));
      return;
    }
    setFileName(file.name);
    setBusy(true);
    try {
      const text = await readText(file);
      setCsv(text);
      const r = await validateBulk(tenant, text);
      setResult(r);
      const platforms = [...new Set(r.rows.filter((row) => row.ok).flatMap((row) => row.post.platforms))];
      const baseUrl = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || window.location.origin;
      const { missing, expired } = await checkPlatformAuth(baseUrl, tenant, platforms);
      setUnconnected([...missing, ...expired]);
    } catch (err) {
      setError(errorText(err, t('publishing.bulk.checkError', 'We could not read that file.')));
    } finally {
      setBusy(false);
    }
  };

  const schedule = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await commitBulk(tenant, csv, skipInvalid);
      setDone(r);
      setResult(null);
    } catch (err) {
      const data = err?.response?.data;
      if (data && data.rows) setResult(data);
      setError(errorText(err, t('publishing.bulk.commitError', 'The posts could not be scheduled.')));
    } finally {
      setBusy(false);
    }
  };

  const summary = result && result.summary;
  const canSchedule = summary && summary.valid > 0 && (summary.invalid === 0 || skipInvalid) && !busy;
  const buttons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaCalendarCheck />, label: t('publishing.bulk.scheduledPosts', 'Scheduled posts'), onClick: () => navigate(`/${tenant}/media/scheduled-posts`) },
    { icon: <FaRss />, label: t('publishing.rss.navLabel', 'RSS feeds'), onClick: () => navigate(`/${tenant}/media/rss-feeds`) },
  ];

  return (
    <div className="pb-page">
      <DesktopActionBar buttons={buttons} />
      <section className="surface">
        <h2>{t('publishing.bulk.title', 'Bulk schedule from a CSV file')}</h2>
        <p className="surface-muted">
          {t('publishing.bulk.intro', 'Plan a week or a month of posts in a spreadsheet, then schedule them all at once. Each row is one post.')}
        </p>
        <ol className="pb-steps">
          <li>{t('publishing.bulk.step1', 'Download the template and open it in Excel, Google Sheets or Numbers.')}</li>
          <li>{t('publishing.bulk.step2', 'Fill in one row per post: date (YYYY-MM-DD), time, platforms and the text. Times are in your organization’s time zone. Media must be public web links.')}</li>
          <li>{t('publishing.bulk.step3', 'Save as CSV and upload it here. We check every row before anything is scheduled.')}</li>
        </ol>
        <div className="pb-actions" style={{ marginTop: 8 }}>
          <a className="pb-btn" href={bulkTemplateUrl(tenant)} download>
            <FaDownload aria-hidden="true" /> {t('publishing.bulk.template', 'Download template')}
          </a>
          <label className="pb-btn primary" htmlFor={inputId}>
            <FaUpload aria-hidden="true" /> {fileName ? t('publishing.bulk.another', 'Upload a different file') : t('publishing.bulk.upload', 'Upload CSV')}
          </label>
          <input id={inputId} type="file" accept=".csv,text/csv" onChange={onFile} style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }} aria-label={t('publishing.bulk.upload', 'Upload CSV')} />
        </div>
      </section>

      {busy && <div className="note info" role="status">{t('publishing.bulk.checking', 'Checking…')}</div>}
      {error && <div className="note error" role="alert">{error}</div>}

      {done && (
        <div className="note ok" role="status" data-testid="bulk-done">
          <p style={{ margin: 0 }}>
            {t('publishing.bulk.done', '{{count}} post(s) scheduled.', { count: done.created - (done.drafts || 0) - (done.inReview || 0) })}
            {done.drafts ? ` ${t('publishing.bulk.doneDrafts', '{{count}} saved as drafts.', { count: done.drafts })}` : ''}
            {done.inReview ? ` ${t('publishing.bulk.doneReview', '{{count}} sent for review; they publish once approved.', { count: done.inReview })}` : ''}
            {done.skipped ? ` ${t('publishing.bulk.doneSkipped', '{{count}} row(s) with errors were skipped.', { count: done.skipped })}` : ''}
          </p>
          <p style={{ margin: '6px 0 0' }}>
            <Link to={`/${tenant}/media/scheduled-posts`}>{t('publishing.bulk.seeScheduled', 'See scheduled posts')}</Link>
            {done.inReview ? <> · <Link to={`/${tenant}/approvals`}>{t('publishing.bulk.seeApprovals', 'Open Approvals')}</Link></> : null}
          </p>
        </div>
      )}

      {result && (
        <section className="surface" data-testid="bulk-preview">
          <h3>{t('publishing.bulk.previewTitle', 'Check {{file}}', { file: fileName || 'CSV' })}</h3>
          <div className="pb-summary">
            <span className="pb-badge ok">{t('publishing.bulk.ready', '{{count}} ready', { count: summary.valid })}</span>
            {summary.invalid > 0 && <span className="pb-badge err">{t('publishing.bulk.withErrors', '{{count}} with errors', { count: summary.invalid })}</span>}
            <span>{t('publishing.bulk.timezone', 'Times are in {{tz}}.', { tz: result.timezone })}</span>
          </div>
          {result.unknownColumns && result.unknownColumns.length > 0 && (
            <div className="note warn" style={{ marginTop: 8 }}>
              {t('publishing.bulk.unknownColumns', 'These columns were ignored: {{cols}}', { cols: result.unknownColumns.join(', ') })}
            </div>
          )}
          {unconnected.length > 0 && (
            <div className="note warn" style={{ marginTop: 8 }} role="status">
              {t('publishing.bulk.unconnected', 'Not connected (or the connection expired): {{list}}. Posts to these platforms will fail until you reconnect them in Settings → Social Media.', { list: unconnected.join(', ') })}
            </div>
          )}
          <div className="pb-table-wrap" style={{ marginTop: 8 }}>
            <table className="pb-table">
              <thead>
                <tr>
                  <th scope="col">{t('publishing.bulk.colRow', 'Row')}</th>
                  <th scope="col">{t('publishing.bulk.colWhen', 'When')}</th>
                  <th scope="col">{t('publishing.bulk.colPlatforms', 'Platforms')}</th>
                  <th scope="col">{t('publishing.bulk.colPost', 'Post')}</th>
                  <th scope="col">{t('publishing.bulk.colCheck', 'Check')}</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row) => (
                  <tr key={row.row} className={row.ok ? '' : 'has-error'}>
                    <td>{row.row}</td>
                    <td>{row.post ? slotLabel(row.post.date, row.post.time, i18n.language) : '—'}</td>
                    <td>{row.post ? row.post.platforms.join(', ') : '—'}</td>
                    <td className="pb-content">
                      {row.post ? row.post.content : ''}
                      {row.post && row.post.mediaUrls.length > 0 && (
                        <div className="pb-ai-meta">{t('publishing.bulk.media', '{{count}} media link(s)', { count: row.post.mediaUrls.length })}</div>
                      )}
                    </td>
                    <td>
                      {row.ok
                        ? <span className={`pb-badge ${row.post.draft ? 'muted' : 'ok'}`}>{row.post.draft ? t('publishing.bulk.draft', 'Draft') : t('publishing.bulk.readyOne', 'Ready')}</span>
                        : <span className="pb-badge err">{t('publishing.bulk.fix', 'Fix')}</span>}
                      {(row.errors.length > 0 || row.warnings.length > 0) && (
                        <ul className="pb-msgs">
                          {row.errors.map((m) => <li key={`e${m}`} className="err">{m}</li>)}
                          {row.warnings.map((m) => <li key={`w${m}`} className="warn">{m}</li>)}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pb-actions" style={{ marginTop: 10 }}>
            {summary.invalid > 0 && summary.valid > 0 && (
              <label className="pb-checks">
                <input type="checkbox" checked={skipInvalid} onChange={(e) => setSkipInvalid(e.target.checked)} />
                {t('publishing.bulk.skip', 'Skip the {{count}} row(s) with errors', { count: summary.invalid })}
              </label>
            )}
            <button type="button" className="pb-btn primary" disabled={!canSchedule} onClick={schedule}>
              <FaCalendarCheck aria-hidden="true" /> {t('publishing.bulk.schedule', 'Schedule {{count}} post(s)', { count: summary.valid })}
            </button>
          </div>
        </section>
      )}

      <AppFooter buttons={buttons} />
    </div>
  );
}
